/**
 * useCarouselStore.js — Carousel Composer state, kept at module level.
 *
 * Photos are local object URLs and live as long as the tab, so the composer's
 * library, selection, slides and settings survive leaving /carousel and coming
 * back, until a full reload. The arrangement is also autosaved to localStorage
 * (keyed by filename) through lib/carousel/persist.js.
 *
 * Components read with useCarousel(selector); selectors must return stable
 * values (plain fields or the memoized select* helpers below). Actions are plain
 * functions and are safe to call from native event listeners.
 */

import { useSyncExternalStore } from 'react';
import {
  DEFAULT_DESIGN, DEFAULT_EXPORT_OPTS, assignPhoto, applyPan, applyPreset, cellCount, cellImgId,
  chunkSlides, deleteSlide, detachPano, duplicateSlide, makePano, makeSlide, moveSlide,
  moveSlideTo, resetCrop, setLayout, slideUnits, swapCells, updateCell, updateDesign, wheelZoom,
} from '../lib/carousel/geometry.js';
import {
  bySequence, computeFamCounts, famKey, makeImgById, rangeSelection, selectFamily,
  toggleSelection, usedMap, visibleFilter,
} from '../lib/carousel/library.js';
import { layoutAsText, parseRecipe } from '../lib/carousel/recipe.js';
import {
  applyState, clearSaved, readSaved, readUI, serializeState, setSaveScope, writeSaved, writeUI,
} from '../lib/carousel/persist.js';
import {
  exportSlide, pickOutputFolder, renderSlidesForZip, saveBlob, slideFilename, slideHasPhotos,
} from '../lib/carousel/exportRender.js';
import { makeZip, zipFilename } from '../lib/carousel/zip.js';
import { isImageFile, loadImageFiles } from '../lib/carousel/imageFiles.js';
import { loadShootPhotos, shootLabel } from '../lib/carousel/hdphotohub.js';
import { NEUTRAL_ADJ, isPlainLook, normalizeLook, viewPhoto } from '../lib/carousel/look.js';
import { track } from '../lib/telemetry';
import { afterRender, scrollToLastSlide, scrollToSlide } from '../components/carousel/dom.js';

let idN = 0;
export const uid = () => 'i' + ++idN;

export const EMPTY_FILTERS = { q: '', fams: [], unused: false, edits: false, onlySel: false };
const EMPTY_MAP = new Map();
const EMPTY_ARR = [];

// The chosen output folder (Chromium showDirectoryPicker handle), mirrored in
// state.outDir so the export note re-renders when it changes.
export const dirRef = { current: null };

let state = {
  library: [],
  sel: [],
  slides: [],
  panos: {},
  design: { ...DEFAULT_DESIGN },
  exportOpts: { ...DEFAULT_EXPORT_OPTS },
  view: 'library',
  size: 2,
  filters: EMPTY_FILTERS,
  assign: null,
  assignPrev: null,
  focusId: null,
  anchorId: null,
  addStyle: 'single',
  panoN: '2',
  peek: null,
  loading: null,
  designOpen: null,
  helpOpen: false,
  fsCollapsed: false,
  exportOpen: false,
  exportRunning: false,
  exportStop: false,
  exportStatus: {},
  outDir: null,
  toast: null,
  savedTick: 0,
  saveNote: false,
  recipe: '',
  // how the review list is laid out on a wide screen
  viewMode: 'focus',            // 'focus' | 'compare' | 'grid'
  // the phone's three steps; desktop ignores this
  step: 'pick',                 // 'pick' | 'review' | 'export'
  // the scope box in the editor
  scope: { on: true, kind: 'waveform' },   // 'waveform' | 'parade' | 'histogram'
  // the export name follows the shoot until someone types their own
  prefixTouched: false,
  // bumped on every edit, so the page knows when to autosave to the server
  rev: 0,
  // the "pick up where you left off?" offer, and whether it was waved away
  draftPrompt: null,
  draftDismissed: false,
  // the open photo editor (framing, rotation, adjustments): { si, ci } | null
  editor: null,
  // the saved carousel this session is editing, if any: { id, title, jobId, updatedAt }
  doc: null,
  docDirty: false,
  docBusy: false,
};

// Not rendered from, so kept out of the reactive state.
export const uiMemory = { sheetTop: 0 };

const listeners = new Set();
let saveTimer = null;
let toastKey = 0;

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getState() {
  return state;
}

// autosave:false for a restore — like the artifact, it is not saved until the next edit,
// so a partial match (only some photos loaded) never overwrites the full save.
function set(patch, { autosave = true, edit = false } = {}) {
  const prev = state;
  const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
  const edited = edit || next.slides !== prev.slides || next.panos !== prev.panos
    || next.design !== prev.design || next.exportOpts !== prev.exportOpts;
  // any edit invalidates what the export sheet says was saved, as the artifact's saveState did
  if (edited && next.exportStatus === prev.exportStatus) next.exportStatus = {};
  // the export name follows whichever shoot supplies the slides, until edited
  if (!next.prefixTouched && (next.slides !== prev.slides || next.library !== prev.library || next.doc !== prev.doc)) {
    const want = defaultPrefix(next);
    if (want && want !== next.exportOpts.prefix) next.exportOpts = { ...next.exportOpts, prefix: want };
  }
  // an edit puts the open saved carousel out of date with its row
  if (edited && autosave && next.doc && next.docDirty === prev.docDirty) next.docDirty = true;
  if (edited && next.rev === prev.rev) next.rev = prev.rev + 1;
  state = next;
  if (edited && autosave) scheduleSave();
  listeners.forEach((fn) => { fn(); });
}

export function useCarousel(selector) {
  return useSyncExternalStore(subscribe, () => selector(state));
}

/* ---------------- memoized selectors ---------------- */

function memo(fn) {
  let lastArgs = null, lastVal;
  return (...args) => {
    if (lastArgs && args.length === lastArgs.length && args.every((a, i) => a === lastArgs[i])) return lastVal;
    lastArgs = args;
    lastVal = fn(...args);
    return lastVal;
  };
}

const imgByIdMemo = memo((library) => makeImgById(library));
const famCountsMemo = memo((library) => computeFamCounts(library));
const usedMemo = memo((slides, panos, library) => usedMap(slides, panos, imgByIdMemo(library)));
const visibleMemo = memo((view, library, filters, used, sel, famCounts) => (
  view === 'library' ? visibleFilter(library, filters, used, sel, famCounts) : library
));
const selIndexMemo = memo((sel) => new Map(sel.map((id, i) => [id, i])));
const savedMemo = memo((_tick, empty) => (empty ? readSaved() : null));

export const selectImgById = (s) => imgByIdMemo(s.library);
export const selectFamCounts = (s) => famCountsMemo(s.library);
export const selectUsed = (s) => usedMemo(s.slides, s.panos, s.library);
export const selectSelIndex = (s) => selIndexMemo(s.sel);
export const selectSaved = (s) => savedMemo(s.savedTick, s.slides.length === 0);
export function selectVisible(s) {
  const f = s.filters;
  return visibleMemo(
    s.view, s.library, f,
    f.unused ? selectUsed(s) : EMPTY_MAP,
    f.onlySel ? s.sel : EMPTY_ARR,
    selectFamCounts(s),
  );
}

function ctx() {
  return { panos: state.panos, imgById: selectImgById(state), design: state.design };
}

/* ---------------- autosave ---------------- */

function writeNow() {
  const s = state;
  const wrote = writeSaved({ slides: s.slides, panos: s.panos, design: s.design, exportOpts: s.exportOpts, library: s.library });
  if (wrote || !s.slides.length) {
    state = { ...state, saveNote: s.slides.length > 0, savedTick: state.savedTick + 1 };
    listeners.forEach((fn) => { fn(); });
  }
}

function scheduleSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { saveTimer = null; writeNow(); }, 300);
}

// Called when the page unmounts, so no timer outlives it.
export function flushSave() {
  if (!saveTimer) return;
  clearTimeout(saveTimer);
  saveTimer = null;
  writeNow();
}

/* ---------------- ui ---------------- */

export function initUI() {
  if (state.designOpen !== null) return;
  const ui = readUI();
  // folded away unless it was opened before: shape, padding and fill are set once
  // and then left alone, so the bar was spending a row of every screen on nothing
  state = { ...state, designOpen: typeof ui.designOpen === 'boolean' ? ui.designOpen : false };
}

export function toast(msg, ms) {
  set({ toast: { msg, ms: ms || 2600, key: ++toastKey, at: Date.now() } });
}

export function setDesignOpen(open, remember) {
  set({ designOpen: open });
  if (remember) writeUI({ designOpen: open });
}

export function setHelpOpen(open) {
  if (state.helpOpen !== open) set({ helpOpen: open });
}

export function toggleFilmstrip() {
  set({ fsCollapsed: !state.fsCollapsed });
}

export function setView(v) {
  const patch = { view: v };
  if (v !== 'library' && state.assign) patch.assign = null;
  set(patch);
}

export function setSize(n) {
  set({ size: Math.min(3, Math.max(0, n)) });
}

export function setFilters(patch) {
  set({ filters: { ...state.filters, ...patch } });
}

export function toggleFam(key) {
  const fams = state.filters.fams;
  setFilters({ fams: fams.indexOf(key) >= 0 ? fams.filter((k) => k !== key) : fams.concat(key) });
}

export function clearFilters() {
  set({ filters: EMPTY_FILTERS });
}

export function setRecipe(text) {
  set({ recipe: text });
}

export function setAddStyle(addStyle) {
  set({ addStyle });
}

export function setPanoN(panoN) {
  set({ panoN });
}

/* ---------------- selection ---------------- */

export function toggleSel(id) {
  set({ sel: toggleSelection(state.sel, id), anchorId: id, focusId: id });
}

export function rangeSelect(id) {
  const order = selectVisible(state).map((im) => im.id);
  const next = rangeSelection(state.sel, order, state.anchorId, id);
  if (!next) { toggleSel(id); return; }
  set({ sel: next, focusId: id });
}

export function selectAllFam(key) {
  set({ sel: selectFamily(state.sel, selectVisible(state), key, selectFamCounts(state)) });
}

export function moveSelAt(i, d) {
  const j = i + d;
  if (j < 0 || j >= state.sel.length) return;
  const sel = state.sel.slice();
  sel.splice(j, 0, sel.splice(i, 1)[0]);
  set({ sel });
}

export function deselectAt(i) {
  set({ sel: state.sel.filter((_, k) => k !== i) });
}

export function swapSel() {
  set({ sel: state.sel.slice().reverse() });
}

export function clearSel() {
  set({ sel: [] });
}

export function focusTile(id, extend) {
  const patch = { focusId: id };
  if (extend && state.sel.indexOf(id) < 0) patch.sel = state.sel.concat(id);
  set(patch);
}

export function famKeyOf(im) {
  return famKey(im, selectFamCounts(state));
}

/* ---------------- photos ---------------- */

// Adds still decoding. Overlapping adds (a second drop before the first lands) share
// one progress count, and the saved carousel is only restored once all have landed.
const loads = new Set();
let loadsPending = 0;

export async function loadFiles(files) {
  const list = Array.from(files || []);
  const total = list.filter(isImageFile).length;
  if (!total) { toast('Those files are not images.'); return; }
  const merge = (recs) => {
    const have = new Set(state.library.map((im) => im.id));
    const add = recs.filter((r) => !have.has(r.id));
    if (add.length) set({ library: state.library.concat(add).sort(bySequence) });
  };
  const job = { done: 0, total };
  const progress = () => {
    let done = 0, tot = 0;
    loads.forEach((j) => { done += j.done; tot += j.total; });
    set({ loading: { done, total: tot } });
  };
  loads.add(job);
  loadsPending++;
  progress();
  let imgs = [];
  try {
    imgs = await loadImageFiles(list, {
      uid,
      onProgress: (done, tot, soFar) => {
        job.done = done;
        job.total = tot;
        progress();
        if (done % 6 === 0) merge(soFar);
      },
    });
  } finally {
    merge(imgs);
    if (!--loadsPending) {
      loads.clear();
      set({ loading: null });
      tryAutoRestore();
    }
  }
}

// A delivered HDPhotoHub shoot, loaded like a drop of files: tiles use the
// portal's cached thumbnails, originals stay on the delivery CDN.
export async function loadShoot(site) {
  const label = shootLabel(site);
  const job = { done: 0, total: (site.media || []).filter((m) => m.type === 'image').length };
  const progress = () => {
    let done = 0, tot = 0;
    loads.forEach((j) => { done += j.done; tot += j.total; });
    set({ loading: { done, total: tot } });
  };
  loads.add(job);
  loadsPending++;
  progress();
  let imgs = [];
  try {
    imgs = await loadShootPhotos(site, {
      uid,
      existing: state.library,
      onProgress: (done, tot) => { job.done = done; job.total = tot; progress(); },
    });
  } finally {
    if (imgs.length) set({ library: state.library.concat(imgs).sort(bySequence) });
    if (!--loadsPending) {
      loads.clear();
      set({ loading: null });
      tryAutoRestore();
    }
  }
  toast(imgs.length
    ? 'Loaded ' + imgs.length + ' photos from ' + label
    : 'Those photos are already loaded.');
  track('carousel.shoot_loaded', { sid: site.sid, photos: imgs.length });
  return imgs.length;
}

// A shoot photo arrives with the thumbnail's aspect; the first full decode knows
// better. Framing is normalised, so nothing on screen moves.
export function updatePhotoDims(id, w, h) {
  const i = state.library.findIndex((im) => im.id === id);
  if (i < 0 || !w || !h) return;
  const im = state.library[i];
  if (im.exactDims && im.srcW === w && im.srcH === h) return;
  const look = normalizeLook(im.look);
  const v = viewPhoto({ ...im, srcW: w, srcH: h }, look);
  const library = state.library.slice();
  library[i] = { ...im, srcW: w, srcH: h, exactDims: true, w: v.w, h: v.h };
  set({ library }, { autosave: false });
}

/* ---------------- a photo's look: rotation + adjustments ---------------- */

// Merged into the photo, so every slide using it follows. w/h become the baked
// size, which is what every crop, seam and export measurement works against.
export function setPhotoLook(id, patch) {
  const i = state.library.findIndex((im) => im.id === id);
  if (i < 0) return;
  const im = state.library[i];
  const cur = normalizeLook(im.look);
  const look = normalizeLook({
    ...cur,
    ...patch,
    adj: { ...cur.adj, ...(patch && patch.adj ? patch.adj : null) },
  });
  const srcW = im.srcW || im.w, srcH = im.srcH || im.h;
  const v = viewPhoto({ ...im, srcW, srcH }, look);
  const library = state.library.slice();
  library[i] = { ...im, srcW, srcH, look, w: v.w, h: v.h };
  set({ library }, { edit: true });
}

export function resetPhotoLook(id) {
  setPhotoLook(id, { rot: 0, corner: 'zoom', adj: NEUTRAL_ADJ });
}

export function photoLookOf(id) {
  const im = state.library.find((x) => x.id === id);
  return normalizeLook(im && im.look);
}

export function hasLook(im) {
  return !!im && !isPlainLook(im.look);
}

/* ---------------- framing (per cell) ---------------- */

export function setCellFit(si, ci, fit) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, updateCell(sl, ci, { fit: fit === 'contain' ? 'contain' : undefined }));
}

// 'none' shows the original on this slide only; 'photo' follows the photo again.
export function setCellLookMode(si, ci, mode) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, updateCell(sl, ci, { look: mode === 'none' ? 'none' : undefined }));
}

export function nudgeCell(si, ci, dx, dy, step = 0.02) {
  const sl = state.slides[si];
  const cell = sl && sl.cells[ci];
  if (!cell) return;
  const clamp = (v) => Math.min(1, Math.max(0, v));
  replaceSlide(si, updateCell(sl, ci, { ox: clamp(cell.ox + dx * step), oy: clamp(cell.oy + dy * step) }));
}

export function centerCell(si, ci) {
  const sl = state.slides[si];
  if (sl && sl.cells[ci]) replaceSlide(si, updateCell(sl, ci, { ox: 0.5, oy: 0.5 }));
}

/* ---------------- the photo editor sheet ---------------- */

export function openEditor(si, ci) {
  const sl = state.slides[si];
  if (!sl) return;
  set({ editor: { si, ci }, helpOpen: false }, { autosave: false });
}

export function closeEditor() {
  if (state.editor) set({ editor: null }, { autosave: false });
}

// A saved carousel carries the rotation and adjustments of the photos it uses.
function libraryWithLooks(looks) {
  if (!looks || !looks.length) return state.library;
  const byId = new Map(looks.map((l) => [l.imgId, l.look]));
  return state.library.map((im) => {
    const look = byId.get(im.id);
    if (!look) return im;
    const srcW = im.srcW || im.w, srcH = im.srcH || im.h;
    const v = viewPhoto({ ...im, srcW, srcH }, look);
    return { ...im, srcW, srcH, look, w: v.w, h: v.h };
  });
}

function tryAutoRestore() {
  if (state.slides.length) return;
  const saved = readSaved();
  if (!saved) return;
  const r = applyState(saved, state.library, uid, { design: state.design, exportOpts: state.exportOpts });
  if (!r) return;
  set({
    slides: r.slides, panos: r.panos, design: r.design, exportOpts: r.exportOpts,
    library: libraryWithLooks(r.looks),
  }, { autosave: false });
  toast('Restored your last carousel · ' + r.slides.length + ' slides', 4200);
}

export function restoreSaved() {
  const saved = readSaved();
  const r = saved ? applyState(saved, state.library, uid, { design: state.design, exportOpts: state.exportOpts }) : null;
  if (!r) { toast('None of those photos are loaded yet.'); return; }
  set({
    slides: r.slides, panos: r.panos, design: r.design, exportOpts: r.exportOpts,
    view: 'carousel', assign: null, library: libraryWithLooks(r.looks),
  }, { autosave: false });
  toast('Restored ' + r.slides.length + ' slides');
}

export function dismissSaved() {
  clearSaved();
  set({ savedTick: state.savedTick + 1 });
}

/* ---------------- adding slides ---------------- */

export function addOne() {
  const s = state;
  let slides, panos = s.panos;
  if (s.addStyle === 'pano') {
    if (!s.sel.length) { toast('Select the wide photo first.'); return; }
    const p = makePano(s.sel[0], parseInt(s.panoN, 10) || 2, uid);
    slides = s.slides.concat(p.slides);
    panos = { ...s.panos, [p.gid]: p.group };
  } else {
    slides = s.slides.concat(makeSlide(s.addStyle, s.sel.slice(0, cellCount(s.addStyle)), uid));
  }
  set({ slides, panos, sel: [] });
  toast('Slide ' + slides.length + ' added · ' + slides.length + ' total');
  if (s.view === 'carousel') afterRender(scrollToLastSlide);
}

export function addSingles() {
  const s = state;
  if (!s.sel.length) { toast('Select photos in the list first.'); return; }
  set({ slides: s.slides.concat(s.sel.map((id) => makeSlide('single', [id], uid))), sel: [] });
}

export function pairAdd(layout) {
  const s = state;
  if (s.sel.length < 2) { toast('Select at least two photos.'); return; }
  set({ slides: s.slides.concat(chunkSlides(layout, s.sel, uid)), sel: [] });
}

/* ---------------- slide edits ---------------- */

function replaceSlide(si, sl) {
  const slides = state.slides.slice();
  slides[si] = sl;
  set({ slides });
}

export function moveSlideAt(si, d) {
  const slides = moveSlide(state.slides, si, d);
  if (slides !== state.slides) set({ slides });
}

export function duplicateAt(si) {
  const sl = state.slides[si];
  if (!sl) return;
  const slides = state.slides.slice();
  slides.splice(si + 1, 0, duplicateSlide(sl, ctx(), uid));
  set({ slides });
}

export function deleteAt(si) {
  set({ slides: deleteSlide(state.slides, si) });
}

export function setLayoutAt(si, layout) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, setLayout(sl, layout, ctx()));
}

export function detachAt(si) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, detachPano(sl, ctx()));
}

export function swapAt(si) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, swapCells(sl));
}

export function setZoomAt(si, ci, zoom) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, updateCell(sl, ci, { zoom }));
}

export function wheelAt(si, ci, deltaY) {
  const sl = state.slides[si];
  const cell = sl && sl.cells[ci];
  if (!cell || sl.pano) return;
  replaceSlide(si, updateCell(sl, ci, { zoom: wheelZoom(cell.zoom, deltaY) }));
}

export function setSplitAt(si, pct) {
  const sl = state.slides[si];
  if (sl) replaceSlide(si, { ...sl, split: pct / 100 });
}

export function resetCropAt(si, ci) {
  set(resetCrop(state.slides, state.panos, si, ci));
}

export function commitPan(si, pan, pos) {
  set(applyPan(state.slides, state.panos, si, pan, pos));
}

function assignWithToast(slides, panos, si, ci, id) {
  const sl = slides[si];
  const r = assignPhoto(slides, panos, si, ci, id);
  if (sl && sl.pano && r.panos !== panos) {
    const im = selectImgById(state)(id);
    if (im) toast('Panorama now uses ' + im.name + '.');
  }
  return r;
}

export function dropPhotoAt(si, ci, id) {
  set(assignWithToast(state.slides, state.panos, si, ci, id));
}

export function clearSlides() {
  if (state.slides.length) set({ slides: [] });
}

/* ---------------- assign mode (the photo picker) ---------------- */

export function openPicker(si, ci) {
  if (!state.library.length) { toast('Load some photos first.'); return; }
  set({
    assign: { si, ci },
    assignPrev: { filters: state.filters },
    filters: EMPTY_FILTERS,
    view: 'library',
  });
}

export function commitAssign(id) {
  const a = state.assign;
  const prev = state.assignPrev;
  const r = a ? assignWithToast(state.slides, state.panos, a.si, a.ci, id) : { slides: state.slides, panos: state.panos };
  set({
    assign: null, assignPrev: null, filters: prev ? prev.filters : state.filters,
    view: 'carousel', slides: r.slides, panos: r.panos,
  });
}

export function cancelAssign() {
  const a = state.assign;
  const prev = state.assignPrev;
  set({ assign: null, assignPrev: null, filters: prev ? prev.filters : state.filters, view: 'carousel' });
  if (a) afterRender(() => scrollToSlide(a.si));
}

export function gotoUse(id) {
  const u = selectUsed(state).get(id);
  if (!u || !u.length) return;
  setView('carousel');
  afterRender(() => scrollToSlide(u[0], true));
}

/* ---------------- peek ---------------- */

export function openPeek(ids) {
  const imgById = selectImgById(state);
  const list = (ids || []).filter((id) => imgById(id)).slice(0, 2);
  if (list.length) set({ peek: list });
}

export function closePeek() {
  if (state.peek) set({ peek: null });
}

export function stepPeek(d) {
  const p = state.peek;
  const vis = selectVisible(state);
  if (!p || p.length !== 1 || !vis.length) return;
  const i = vis.findIndex((im) => im.id === p[0]);
  if (i < 0) return;
  const j = Math.min(vis.length - 1, Math.max(0, i + d));
  if (j === i) return;
  set({ focusId: vis[j].id, peek: [vis[j].id] });
}

/* ---------------- design ---------------- */

export function setPreset(key) {
  set({ design: applyPreset(state.design, key) });
}

export function setDesign(patch) {
  set({ design: updateDesign(state.design, patch) });
}

export function setExportOpts(patch) {
  const next = { exportOpts: { ...state.exportOpts, ...patch } };
  // typing a name takes it off the shoot
  if (patch && patch.prefix !== undefined) next.prefixTouched = true;
  set(next);
}

/* ---------------- view mode, steps, scope ---------------- */

export function setViewMode(viewMode) {
  if (state.viewMode !== viewMode) set({ viewMode }, { autosave: false });
  writeUI({ viewMode });
}

export function setStep(step) {
  if (state.step === step) return;
  // the steps are the phone's version of the Library / Carousel switch
  set({ step, view: step === 'pick' ? 'library' : 'carousel', exportOpen: false }, { autosave: false });
}

export function setScope(patch) {
  set({ scope: { ...state.scope, ...patch } }, { autosave: false });
  writeUI({ scope: { ...state.scope, ...patch } });
}

/* ---------------- reordering ---------------- */

// Drag or arrows, from the strip or a card: a panorama run moves as one block.
export function moveSlideToIndex(si, target) {
  const slides = moveSlideTo(state.slides, si, target);
  if (slides !== state.slides) set({ slides });
}

export function selectUnits(s = state) {
  return slideUnits(s.slides);
}

/* ---------------- export naming ---------------- */

const slug = (t) => String(t || '')
  .toLowerCase()
  .replace(/,.*$/, '')                    // "4 Rincon Point Ln, Carpinteria" -> the street
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 48);

/**
 * car_<property address>, from the shoot that supplies the most slides. Local
 * files have no address, so those fall back to the carousel's saved name.
 */
export function defaultPrefix(s = state) {
  const imgById = makeImgById(s.library);
  const counts = new Map();
  s.slides.forEach((sl) => {
    sl.cells.forEach((_, ci) => {
      const id = cellImgId(sl, ci, s.panos, imgById);
      const im = id ? imgById(id) : null;
      if (im && im.source === 'hdph' && im.shoot) counts.set(im.shoot, (counts.get(im.shoot) || 0) + 1);
    });
  });
  let best = '', most = 0;
  counts.forEach((n, label) => { if (n > most) { most = n; best = label; } });
  return 'car_' + (slug(best) || slug(s.doc && s.doc.title) || 'untitled');
}

/* ---------------- recipe ---------------- */

export function buildRecipe(append) {
  if (!state.library.length) { toast('Load your photos first, then build.'); return false; }
  const r = parseRecipe(state.recipe, state.library, uid);
  if (!r.slides.length) { toast('No photos matched those lines.'); return false; }
  set({
    slides: append ? state.slides.concat(r.slides) : r.slides,
    panos: append ? { ...state.panos, ...r.panos } : r.panos,
  });
  toast(
    r.slides.length + ' slide(s) built' + (r.missing.length ? ' · no match for: ' + r.missing.slice(0, 4).join(', ') : ''),
    r.missing.length ? 5200 : 2600,
  );
  return true;
}

export async function copyRecipe() {
  const t = layoutAsText(state.slides, state.panos, selectImgById(state));
  if (!t) { toast('No slides to copy.'); return; }
  try {
    await navigator.clipboard.writeText(t);
    toast('Layout copied to the clipboard.');
  } catch {
    set({ recipe: t });
    toast('Copied into the box below.');
  }
}

/* ---------------- export ---------------- */

function exportCtx() {
  const s = state;
  return { design: s.design, exportOpts: s.exportOpts, panos: s.panos, imgById: selectImgById(s) };
}

export function exportState(s, si) {
  const sl = s.slides[si];
  if (!slideHasPhotos(sl, s.panos, selectImgById(s))) return 'empty';
  return s.exportStatus[sl.id] || 'ready';
}

export function pendingSlides(s) {
  return s.slides.map((_, i) => i).filter((i) => { const st = exportState(s, i); return st !== 'saved' && st !== 'empty'; });
}

// saveBlob forgets a folder it could not write to, so outDir follows dirRef here too
function setStatus(id, st) {
  set({ exportStatus: { ...state.exportStatus, [id]: st }, outDir: dirRef.current });
}

export function openExport() {
  if (!state.slides.length) { toast('Build some slides first.'); return; }
  set({ exportOpen: true, helpOpen: false });
}

export function closeExport() {
  set({ exportOpen: false, exportStop: state.exportRunning ? true : state.exportStop });
}

export function stopExport() {
  if (state.exportRunning) set({ exportStop: true });
}

export async function pickFolder() {
  const ok = await pickOutputFolder(dirRef);
  if (ok) toast('Slides will be written to ' + (dirRef.current.name || 'the chosen folder'));
  set({ outDir: dirRef.current });
}

// logRun: the Save all / Save remaining button, logged once when the run ends.
// In zip mode every slide is rendered first, then delivered as one file, so the
// browser asks about one download instead of one per slide.
export async function runExport(list, { logRun = false, forceFiles = false } = {}) {
  if (state.exportRunning || !list.length) return;
  const zipMode = !forceFiles && state.exportOpts.bundle === 'zip';
  set({ exportRunning: true, exportStop: false });
  let saved = 0, skipped = 0, failed = 0;

  if (zipMode) {
    const c = exportCtx();
    const mark = (si, st) => {
      if (st === 'failed') failed++;
      const sl = state.slides[si];
      if (!sl) return;
      const row = document.querySelector('.cc-export .xrow[data-si="' + si + '"]');
      if (row) row.scrollIntoView({ block: 'nearest' });
      setStatus(sl.id, st);
    };
    // Stopping mid-run zips what is already rendered; the rest stay Ready.
    const files = await renderSlidesForZip(state.slides, list, c, mark, () => state.exportStop);
    if (files.length) {
      const name = zipFilename(c.exportOpts.prefix);
      let result = 'failed';
      try {
        const zip = await makeZip(files.map((f) => ({ name: f.name, blob: f.blob })));
        result = await saveBlob(zip, name, dirRef);
      } catch { result = 'failed'; }
      const st = result === 'declined' ? 'skipped' : result;
      files.forEach((f) => { const sl = state.slides[f.si]; if (sl) setStatus(sl.id, st); });
      if (st === 'saved') saved = files.length; else if (st === 'skipped') skipped = files.length;
      toast(st === 'saved'
        ? 'Saved ' + name + ' · ' + files.length + ' slides'
        : st === 'skipped' ? 'Save cancelled' : 'Could not write ' + name, 4200);
    } else {
      toast('Nothing to save.');
    }
  } else {
    for (const si of list) {
      if (state.exportStop) break;
      const sl = state.slides[si];
      if (!sl) continue;
      const row = document.querySelector('.cc-export .xrow[data-si="' + si + '"]');
      if (row) row.scrollIntoView({ block: 'nearest' });
      const c = exportCtx();
      if (slideHasPhotos(sl, c.panos, c.imgById)) setStatus(sl.id, 'working');
      const r = await exportSlide(sl, si, c, dirRef);
      const st = r === 'declined' ? 'skipped' : r;
      setStatus(sl.id, st);
      if (st === 'saved') saved++; else if (st === 'skipped') skipped++; else if (st === 'failed') failed++;
    }
    if (list.length > 1) {
      toast('Saved ' + saved + (skipped ? ' · skipped ' + skipped : '') + (failed ? ' · ' + failed + ' failed' : ''), 4200);
    }
  }

  set({ exportRunning: false, exportStop: false });
  if (logRun) {
    // Behaviour, not an audit trail: exporting writes no row anywhere.
    const { exportOpts } = state;
    track('carousel.export', {
      slides: list.length, saved, skipped, failed,
      qual: exportOpts.qual, type: exportOpts.type, bundle: exportOpts.bundle,
    });
  }
}

// The per-slide button on a card: same engine, reported by toast, not logged.
export async function exportOne(si) {
  const sl = state.slides[si];
  if (!sl) return;
  const c = exportCtx();
  const name = slideFilename(si, c.exportOpts);
  if (!slideHasPhotos(sl, c.panos, c.imgById)) {
    toast('That slide has no photos yet');
    setStatus(sl.id, 'empty');
    return;
  }
  toast('Rendering ' + name + '…', 8000);
  const r = await exportSlide(sl, si, c, dirRef);
  toast({ saved: 'Saved ' + name, declined: 'Save cancelled', failed: 'Could not save ' + name, empty: 'That slide has no photos yet' }[r]);
  setStatus(sl.id, r === 'declined' ? 'skipped' : r);
}

/* ---------------- the per-user autosave (carousel_drafts) ---------------- */

// The browser copy is keyed by whoever is signed in, so two people on one
// machine never share an autosave.
export function setSaveScopeFor(profileId) {
  // The restore bar reads localStorage through a memo keyed on savedTick, and on a
  // cold load that read happens BEFORE the draft GET tells us whose browser this
  // is. The key it read is now the wrong one, so bump the tick: without this the
  // bar is decided against the unscoped key for the rest of the session and never
  // appears for anything this build saved.
  if (setSaveScope(profileId)) set({ savedTick: state.savedTick + 1 }, { autosave: false });
}

export function hasWork(s = state) {
  return s.slides.length > 0 || s.sel.length > 0;
}

// Shoots needed to bring this state back: the ones its slides use, plus any the
// selection tray is holding.
function draftSource(s = state) {
  const base = docSource(s);
  const imgById = selectImgById(s);
  const shoots = new Map(base.shoots.map((x) => [x.sid, x.label]));
  let hasLocal = base.hasLocal;
  s.sel.forEach((id) => {
    const im = imgById(id);
    if (!im) return;
    if (im.source === 'hdph') shoots.set(im.sid, im.shoot || '');
    else hasLocal = true;
  });
  return { shoots: Array.from(shoots, ([sid, label]) => ({ sid, label })), hasLocal };
}

// What the page sends up every few seconds. References only — never an image.
export function draftPayload(s = state) {
  return {
    data: serializeState({
      slides: s.slides, panos: s.panos, design: s.design, exportOpts: s.exportOpts,
      library: s.library, sel: s.sel,
    }),
    source: draftSource(s),
    slideCount: s.slides.length,
  };
}

// Offers the draft, but never over work already on screen.
export function offerDraft(draft) {
  if (!draft || !draft.data || !draft.data.slides || !draft.data.slides.length) return;
  if (state.draftDismissed || state.draftPrompt) return;
  if (state.slides.length || state.library.length) return;
  set({
    draftPrompt: {
      data: draft.data,
      source: draft.source || {},
      slideCount: draft.slide_count || draft.data.slides.length,
      updatedAt: draft.updated_at || null,
    },
  }, { autosave: false });
}

export function dismissDraftPrompt() {
  set({ draftPrompt: null, draftDismissed: true }, { autosave: false });
}

// Puts a draft on screen. Its shoots must be loaded first — the banner does that.
export function applyDraft(saved) {
  const r = applyState(saved, state.library, uid, { design: state.design, exportOpts: state.exportOpts });
  if (!r) {
    toast('None of those photos could be loaded.', 4200);
    set({ draftPrompt: null, draftDismissed: true }, { autosave: false });
    return false;
  }
  set({
    slides: r.slides, panos: r.panos, design: r.design, exportOpts: r.exportOpts,
    sel: r.sel || [], library: libraryWithLooks(r.looks),
    view: 'carousel', assign: null, assignPrev: null,
    draftPrompt: null, draftDismissed: true,
  }, { autosave: false });
  const gaps = r.slides.reduce((n, sl) => n + sl.cells.filter((c) => !c).length, 0);
  toast('Picked up where you left off · ' + r.slides.length + ' slides'
    + (gaps ? ' · ' + gaps + ' photo(s) missing' : ''), gaps ? 5200 : 2600);
  track('carousel.draft_restored', { slides: r.slides.length, gaps });
  return true;
}

/* ---------------- saved carousels (the `carousels` table) ---------------- */

// What it takes to reopen this carousel elsewhere: the shoots it draws on, and
// whether any local file is in it (those cannot follow you to another machine).
export function docSource(s = state) {
  const imgById = selectImgById(s);
  const shoots = new Map();
  let hasLocal = false;
  s.slides.forEach((sl) => {
    sl.cells.forEach((_, ci) => {
      const id = cellImgId(sl, ci, s.panos, imgById);
      const im = id ? imgById(id) : null;
      if (!im) return;
      if (im.source === 'hdph') shoots.set(im.sid, im.shoot || '');
      else hasLocal = true;
    });
  });
  return { shoots: Array.from(shoots, ([sid, label]) => ({ sid, label })), hasLocal };
}

// The row body for a save. `data` is the same blob localStorage holds.
export function docPayload(title, jobId) {
  const s = state;
  return {
    title: String(title || '').trim().slice(0, 200) || 'Untitled carousel',
    job_id: jobId || null,
    source: docSource(s),
    data: serializeState({
      slides: s.slides, panos: s.panos, design: s.design, exportOpts: s.exportOpts, library: s.library,
    }),
    slide_count: s.slides.length,
  };
}

export function setDocBusy(docBusy) {
  if (state.docBusy !== docBusy) set({ docBusy }, { autosave: false });
}

// After a successful create/update: the row is now what is on screen.
export function docSaved(row, { quiet = false } = {}) {
  set({
    doc: { id: row.id, title: row.title, jobId: row.job_id || null, updatedAt: row.updated_at || null },
    docDirty: false,
  }, { autosave: false });
  if (!quiet) toast('Saved “' + row.title + '”');
}

export function closeDoc() {
  if (state.doc) set({ doc: null, docDirty: false }, { autosave: false });
}

// Puts a saved carousel on screen. Its photos must be loaded first — the
// carousels dialog loads the shoots named in row.source before calling this.
export function applyDoc(row) {
  const r = applyState(row.data, state.library, uid, { design: state.design, exportOpts: state.exportOpts });
  if (!r) {
    toast('None of that carousel’s photos are loaded yet.', 4200);
    return false;
  }
  set({
    slides: r.slides, panos: r.panos, design: r.design, exportOpts: r.exportOpts,
    view: 'carousel', assign: null, assignPrev: null, sel: [], library: libraryWithLooks(r.looks),
    doc: { id: row.id, title: row.title, jobId: row.job_id || null, updatedAt: row.updated_at || null },
    docDirty: false,
  }, { autosave: false });
  const gaps = r.slides.reduce((n, sl) => n + sl.cells.filter((c) => !c).length, 0);
  toast('Opened “' + row.title + '” · ' + r.slides.length + ' slides'
    + (gaps ? ' · ' + gaps + ' photo(s) missing' : ''), gaps ? 5200 : 2600);
  track('carousel.opened', { slides: r.slides.length, gaps });
  return true;
}
